
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  commentsFields,
  participantsConditionsFields,
  TuitionFormData,
  vendorInfoFields,
  fieldApiIdMap,
  commentTypeMap,
  personalInfoFields,
  approverInfoFields,
  courseInfoFields,
} from "../../../config/tuitionFormConfig";
import { useNavigate, useLocation } from "react-router-dom";
import TuitionApplyForm from "./TuitionApplyForm";
import {
  fetchTuitionEmployeeDetails,
  saveTuitionEmployeeDetails,
  searchAltNuid,
  clearAltNuidError,
  updateManagerDetails,
  submitApplicationForm,
  clearAltNuidResult,
  clearAllErrors,
} from "../../../store/slices/Tuition/tuitionApplySlice";
import Loader from "../../../components/common/Loader";
import { validateForm } from "../../../utils/validationEngine";
import { validateTuitionForm } from "../../../utils/tuitionValidationEngine";
import { safeGet } from "../../../utils/safeGet";
import {
  fetchEmployeeDetailsAdmin,
  saveTuitionEmployeeDetailsAdmin,
  submitApplicationDetailsAdmin,
  updateManagerDetailsAdmin,
} from "../../../store/slices/Tuition/tuitionAdminSlice";
import { trackFormSubmit, trackFormSave } from '../../../services/analytics';
import { submitCorrectedErrors } from "../../../store/slices/Tuition/administerSlice";
import { buildErrorCorrectionPayload } from '../../../utils/buildErrorCorrectionPayload';


const extractManagerFieldValues = (values: Partial<TuitionFormData>) => {
  return {
    formMgrPhone: values?.mgrPhone ?? "",
    formAltPhone: values?.altPhone ?? "",
    formAltNuid: (values?.alternateNuid ?? "").trim(),
  };
};

const extractExistingFieldValues = (formData: any) => {
  return {
    existingMgrPhone: formData?.mgrPhone ?? "",
    existingAltPhone: formData?.altPhone ?? "",
    existingAltNuid: String(formData?.alternateNuid ?? "").trim(),
  };
};

// ============================================================================
// HELPER: Detect field changes
// ============================================================================

const detectManagerChanges = (
  formValues: ReturnType<typeof extractManagerFieldValues>,
  existingValues: ReturnType<typeof extractExistingFieldValues>
) => {
  return {
    managerPhoneChanged:
      formValues.formMgrPhone !== existingValues.existingMgrPhone,
    altManagerPhoneChanged:
      formValues.formAltPhone !== existingValues.existingAltPhone,
    altManagerNuidChanged:
      formValues.formAltNuid !== existingValues.existingAltNuid &&
      formValues.formAltNuid !== "",
  };
};

// ============================================================================
// HELPER: Check if any manager fields changed
// ============================================================================

const hasAnyManagerChanges = (
  changes: ReturnType<typeof detectManagerChanges>
): boolean => {
  return (
    changes.managerPhoneChanged ||
    changes.altManagerPhoneChanged ||
    changes.altManagerNuidChanged
  );
};

// ============================================================================
// HELPER: Build manager payload
// ============================================================================

const buildManagerPayload = (
  formValues: ReturnType<typeof extractManagerFieldValues>,
  existingValues: ReturnType<typeof extractExistingFieldValues>,
  changes: ReturnType<typeof detectManagerChanges>,
  formData: any
) => {
  const payload: any = {};

  if (changes.managerPhoneChanged) {
    payload.managerPhone =
      typeof formValues.formMgrPhone === "string" &&
      formValues.formMgrPhone === ""
        ? null
        : formValues.formMgrPhone ?? existingValues.existingMgrPhone ?? null;
  }

  // Determine the effective altManagerNuid (from form or existing data)
  const effectiveAltNuid = formValues.formAltNuid || existingValues.existingAltNuid;

  // Only include altManagerPhone if there's a valid altManagerNuid
  if (changes.altManagerPhoneChanged && effectiveAltNuid) {
    payload.altManagerPhone =
      typeof formValues.formAltPhone === "string" &&
      formValues.formAltPhone === ""
        ? null
        : formValues.formAltPhone ?? existingValues.existingAltPhone ?? null;
  }

  // Only include altManagerNuid if there's a valid value
  if (changes.altManagerNuidChanged) {
    payload.altManagerNuid =
      formValues.formAltNuid === "" ? null : formValues.formAltNuid;
  } else if (effectiveAltNuid) {
    // Only include existing altNuid if it has a valid value
    payload.altManagerNuid = formData?.altNuid ?? null;
  }

  return payload;
};

// ============================================================================
// HELPER: Reset manager search state
// ============================================================================

type ManagerStateSetters = {
  setAlternateNuidInput: (val: string) => void;
  setIsSearchSuccessful: (val: boolean) => void;
  setClearAltNuidSignal: (fn: (s: number) => number) => void;
};

const resetManagerSearchState = (stateSetters: ManagerStateSetters) => {
  stateSetters.setAlternateNuidInput("");
  stateSetters.setIsSearchSuccessful(false);
  stateSetters.setClearAltNuidSignal((s) => s + 1);
};

// ============================================================================
// HELPER: Perform manager update
// ============================================================================

const performManagerUpdate = async (
  managerPayload: any,
  dispatch: any,
  stateSetters: ManagerStateSetters,
  isAdminCreate: boolean,
  values:any,
  formData:any
): Promise<boolean> => {
  // values: current form values for step 3; formData: existing employee details
  try {
    if (isAdminCreate) {
      await dispatch(
        updateManagerDetailsAdmin({ data: managerPayload, nuid: values?.empNuid })
      ).unwrap();
      const empIdStr = String(safeGet(formData?.empId, safeGet(values?.empId, "")));
      if (empIdStr) {
        dispatch(fetchEmployeeDetailsAdmin({ empId: empIdStr }));
      }
    } else {
      await dispatch(updateManagerDetails(managerPayload)).unwrap();
      dispatch(fetchTuitionEmployeeDetails());
      resetManagerSearchState(stateSetters);
    }
    return true;
  } catch (err) {
    console.error("Update manager details failed", err);
    resetManagerSearchState(stateSetters);
    return false;
  }
};
// ============================================================================
// MAIN HANDLER: Step 3 Save
// ============================================================================

const handleStep3Save = async (
  values: Partial<TuitionFormData>,
  formData: any,
  dispatch: any,
  setValidationErrors: (errors: Record<string, string>) => void,
  stateSetters: ManagerStateSetters,
  approverInfoFields: any[],
  isAdminCreate: boolean
): Promise<boolean> => {
  const validationResult = validateForm(
    approverInfoFields,
    values as Record<string, any>
  );

  if (!validationResult.isValid) {
    setValidationErrors(validationResult.errors);
    console.log("Validation failed on step 3:", validationResult.errors);
    return false;
  }

  const formValues = extractManagerFieldValues(values);
  const existingValues = extractExistingFieldValues(formData);
  const changes = detectManagerChanges(formValues, existingValues);

  if (!hasAnyManagerChanges(changes)) {
    console.warn("No manager fields changed — skipping manager update.");
    return true;
  }
  const managerPayload = buildManagerPayload(
    formValues,
    existingValues,
    changes,
    formData
  );
  // Admin flows should not include alternate approver NUID in the manager update payload
  if (isAdminCreate && managerPayload?.altManagerNuid !== undefined) {
    delete managerPayload.altManagerNuid;
  }
  return performManagerUpdate(
    managerPayload,
    dispatch,
    stateSetters,
    isAdminCreate,
    values,
    formData
  );
};

// ============================================================================
// HELPER: Get field names for a specific step
// ============================================================================

const getCurrentStepFieldNames = (step: number): string[] => {
  switch (step) {
    case 1:
      return personalInfoFields.map(f => f.name);
    case 3:
      return approverInfoFields.map(f => f.name);
    case 4:
      return vendorInfoFields.map(f => f.name);
    case 5:
      // For step 5 (class info), return base field names
      return courseInfoFields.map(f => f.name);
    case 6:
      return commentsFields.map(f => f.name);
    default:
      return [];
  }
};

// ============================================================================
// TuitionApplyContainer Component
// ============================================================================

type FormMode = "employee" | "admin";

const TuitionApplyContainer: React.FC<{ mode?: FormMode }> = ({
  mode = "employee",
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAdminCreateApplicationError, setShowAdminCreateApplicationError] = useState(false);
  const [adminCreateApplicationErrorMessage, setAdminCreateApplicationErrorMessage] = useState<string>("");
  const [isSearchSuccessful, setIsSearchSuccessful] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [alternateNuidInput, setAlternateNuidInput] = useState<string>("");
  const [clearAltNuidSignal, setClearAltNuidSignal] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    console.log('[DEBUG] validationErrors state updated:', validationErrors);
  }, [validationErrors]);
  // Move this useEffect after errorCorrectionMode declaration to avoid ReferenceError
  const [initialStep, setInitialStep] = useState<number | null>(null);
  const [errorCorrectionMode, setErrorCorrectionMode] = useState<boolean>(false);
  const [errorCorrectionErrors, setErrorCorrectionErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    console.log('[DEBUG] errorCorrectionMode state updated:', errorCorrectionMode);
  }, [errorCorrectionMode]);
  // Add formValues state for error correction mode
  const [formValues, setFormValues] = useState<TuitionFormData | {}>({});
  const isAdminCreate = mode === "admin";
  const {
    employeeDetails: formData,
    loading,
    error,
    savePatchError,
    updateManagerDetailsError,
    altNuidResult,
    altNuidError,
    // isEligible,
  } = useAppSelector((state) => state.tuitionApply);

  // Admin slice loading flags (used to show loader during admin submits)
  // Guard against undefined store slice in tests by defaulting to an empty object
  const adminSlice = safeGet(useAppSelector((state) => (state as any).tuitionAdmin), {});
  const { loading: adminLoading = false, employeeDetailsLoading = false, error: adminError = null } =
    adminSlice;

  const effectiveSavePatchError = mode === "admin" ? adminError : savePatchError;
  // Store the original API object for error correction mode
  const [originalApiObject, setOriginalApiObject] = useState<any>(null);
  // Handle navigation state for error correction

  // Helper to handle setting validation errors and error correction mode
  const handleNavValidationErrors = (navValidationErrors: any) => {
    console.log('[DEBUG] Setting validationErrors from navValidationErrors:', navValidationErrors);
    setValidationErrors(navValidationErrors);
    setErrorCorrectionErrors(navValidationErrors);
    setErrorCorrectionMode(true);
    setTimeout(() => {
      console.log('[DEBUG] validationErrors state after navValidationErrors:', navValidationErrors);
    }, 100);
  };

  // Helper to handle setting field error
  const handleFieldError = (fieldError: any) => {
    const errors = { [fieldError.fieldName]: fieldError.errorMessage };
    console.log('[DEBUG] Setting validationErrors from fieldError:', errors);
    setValidationErrors(errors);
    setErrorCorrectionErrors(errors);
    setErrorCorrectionMode(true);
  };

  // Helper to merge form values
  const mergeFormValues = (initialValues: any, classCount: any, apiStep4: any, apiStep5: any) => {
    let mergedFormValues = { ...initialValues, classCount };
    if (apiStep4 && typeof apiStep4 === 'object') {
      mergedFormValues = { ...mergedFormValues, ...apiStep4 };
    }
    if (apiStep5 && typeof apiStep5 === 'object') {
      mergedFormValues = { ...mergedFormValues, ...apiStep5 };
    }
    if (classCount && errorCorrectionMode) {
      mergedFormValues.classCount = classCount;
    }
    return mergedFormValues;
  };

  useEffect(() => {
    if (!location.state) return;
    const {
      targetStep,
      fieldError,
      validationErrors: navValidationErrors,
      applicationId,
      employeeId,
      classCount,
      initialValues,
      apiStep4,
      apiStep5,
      apiObject,
    } = location.state;
    console.log('🔍 Navigation state received:', {
      targetStep,
      fieldError,
      navValidationErrors,
      applicationId,
      employeeId,
      classCount,
      initialValues,
      apiStep4,
      apiStep5,
      apiObject,
    });
    if (!targetStep) return;
    setInitialStep(targetStep);
    if (navValidationErrors && typeof navValidationErrors === 'object') {
      handleNavValidationErrors(navValidationErrors);
    } else if (fieldError) {
      handleFieldError(fieldError);
    }
    dispatch(clearAllErrors());
    setFormValues(() => mergeFormValues(initialValues, classCount, apiStep4, apiStep5));
    if (apiObject) {
      setOriginalApiObject(apiObject);
    }
    if (applicationId) {
      // Fetch application data logic can be added here
    }
    if (employeeId) {
      dispatch(fetchEmployeeDetailsAdmin({ empId: employeeId }));
    }
  }, [location.state, dispatch, errorCorrectionMode]);

  useEffect(() => {
    // Skip fetching employee details for Admin create flow
    if (!isAdminCreate && !formData && !loading && !error) {
      dispatch(fetchTuitionEmployeeDetails());
    }
  }, [dispatch, isAdminCreate, formData, loading, error]);

  // ---- Helpers (move outside component) ---- //

  const isFeeField = (fieldName?: string): boolean => {
    if (!fieldName) return false;
    const feeFieldNames = [
      "estimatedTuitionFee",
      "courseCredit",
      "totalNumberOfMiles",
      "estimatedBooksFee",
      "estimatedLabFee",
      "estimatedRegistrationFee",
      "estimatedOtherFee",
      "estimatedAirfareBaggageFee",
      "estimatedParkTollFee",
      "estimatedAutoRentalFee",
      "estimatedPublicTransportFee",
      "estimatedHotelFee",
    ];
    return feeFieldNames.some(f =>
      fieldName.toLowerCase().includes(f.toLowerCase())
    );
  };

  const isValidValue = (value: any, fieldName?: string): boolean => {
    if (isFeeField(fieldName) && (value === 0 || value === "0" || Number.parseFloat(value) === 0)) {
      return false;
    }
    return value !== "" && value !== null && value !== undefined && value !== "undefined";
  };

  const removeEmptyValues = (obj: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => isValidValue(value, key))
    );

  const hasValidValues = (obj: Record<string, any>) =>
    Object.entries(obj).some(([key, value]) => isValidValue(value, key));

  // ---- Builders ---- //

  const buildSchoolInfo = (
    values: TuitionFormData,
    isAdminCreate: boolean
  ) => {
    const schoolInfoObj = Object.fromEntries(
      vendorInfoFields
        .map(field => [field.name, values[field.name]])
        .filter(([, value]) => isValidValue(value))
    );
    const dateFields = vendorInfoFields
      .filter(f => f.type === "date")
      .map(f => f.name);
    const schoolInfo = removeEmptyValues(
      formatDatesInObject(schoolInfoObj, dateFields)
    );
    if (schoolInfo.degreeCompletionYear === "Non Degree Program") {
      schoolInfo.degreeCompletionYear = "-100";
    }
    if (isAdminCreate && values.applicationYear) {
      schoolInfo.applicationYear = values.applicationYear;
    }
    return schoolInfo;
  };

  const buildCourseList = (values: TuitionFormData) => {
    const courseDateFields = ["courseStartDate", "courseEndDate"];
    return (values.classes || [])
      .map(({id,index, classSchoolName, ...rest }: Record<string, any>) => {
        const data = classSchoolName
          ? { ...rest, schoolName: classSchoolName }
          : rest;
        return removeEmptyValues(
          formatDatesInObject(data, courseDateFields)
        );
      })
      .filter(hasValidValues);
  };

  const buildCommentsList = (values: TuitionFormData) =>
    commentsFields
      .map(field => ({
        comments: values[field.name] ?? "",
        commentType: commentTypeMap[field.name] ?? "",
      }))
      .filter(item => isValidValue(item.comments));

  const buildCheckboxList = (values: TuitionFormData) =>
    participantsConditionsFields.map(field => ({
      id: fieldApiIdMap[field.name],
      isSelected: Boolean(
        values[fieldApiIdMap[field.name] as keyof TuitionFormData]
      ),
    }));

  const buildSubmitPayload = (
    schoolInfo: any,
    courseList: any[],
    commentsList: any[],
    checkboxList: any[]
  ) => {
    const payload: Record<string, any> = {};
    if (hasValidValues(schoolInfo)) payload.schoolInfo = schoolInfo;
    if (courseList.length) payload.courseList = courseList;
    if (commentsList.length) payload.commentsList = commentsList;
    if (checkboxList.length) payload.checkboxList = checkboxList;
    return payload;
  };
// Helper to map API errors to validationErrors
function mapApiErrors(errors: any, message?: string): Record<string, string> {
  if (errors) {
    if (Array.isArray(errors)) {
      return { globalError: errors.join(', ') };
    } else if (typeof errors === 'string') {
      return { globalError: errors };
    } else if (typeof errors === 'object') {
      return { ...errors };
    }
  } else if (message) {
    return { globalError: message };
  }
  return { globalError: 'Submission failed.' };
}
  // ---- Submission Handlers ---- //

  const handleErrorCorrection = async ({
    dispatch,
    location,
    errorCorrectionPayload,
    navigate,
    setShowSuccess,
    setShowAdminCreateApplicationError,
    setAdminCreateApplicationErrorMessage,
  }: any) => {
    const emplApplErrId = location.state?.emplApplErrId;
    if (!emplApplErrId) {
      setShowSuccess(false);
      setAdminCreateApplicationErrorMessage('Missing error record ID for error correction submit');
      setShowAdminCreateApplicationError(true);
      return;
    }
    const result = await dispatch(
      submitCorrectedErrors({ emplApplErrId, payload: errorCorrectionPayload })
    );
    if (result.type.endsWith('/fulfilled')) {
      // If the API returns a status property and it's false, treat as error
      const response = result.payload;
      if (response && typeof response === 'object' && response.success === false) {
        setShowSuccess(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Map API errors to validationErrors for TuitionErrorBanner display
        const apiErrors = mapApiErrors(response.errors, response.message);
        setValidationErrors(apiErrors);
        setErrorCorrectionErrors(apiErrors);
        return;
      }
      setShowSuccess(true);
      // setShowAdminCreateApplicationError(false);
      // Set a flag for toast after navigation
      sessionStorage.setItem('tuitionCorrectionSuccess', '1');
      navigate('/admin/administer');
      return;
    }
    const errorMsg =
      typeof result.payload === 'string'
        ? result.payload
        : 'Failed to submit corrected errors';
    setShowSuccess(false);
    setAdminCreateApplicationErrorMessage(errorMsg);
    setShowAdminCreateApplicationError(true);
  };

  const handleAdminSubmit = async (dispatch: any, values: TuitionFormData, payload: any, handlers: any) => {
    const result = await dispatch(
      submitApplicationDetailsAdmin({
        payload,
        nuid: values?.empNuid,
      })
    );
    if (submitApplicationDetailsAdmin.fulfilled.match(result)) {
      handlers.setShowSuccess(true);
      handlers.setShowAdminCreateApplicationError(false);
      return;
    }
    const errorMsg =
      typeof result.payload === 'string'
        ? result.payload
        : 'Failed to submit application';
    handlers.setShowSuccess(false);
    handlers.setAdminCreateApplicationErrorMessage(errorMsg);
    handlers.setShowAdminCreateApplicationError(true);
  };

  const handleUserSubmit = async (dispatch: any, payload: any, handlers: any) => {
    const result = await dispatch(submitApplicationForm(payload));
    if (submitApplicationForm.fulfilled.match(result)) {
      handlers.setShowSuccess(true);
      handlers.setShowAdminCreateApplicationError(false);
      return;
    }
    const errorMsg =
      typeof result.payload === 'string'
        ? result.payload
        : 'Failed to submit application';
    handlers.setShowSuccess(false);
    handlers.setAdminCreateApplicationErrorMessage(errorMsg);
    handlers.setShowAdminCreateApplicationError(true);
  };

  // ---- Main Function ---- //

  const handleSubmit = async (values: TuitionFormData) => {
    console.log(' Analytics: Tuition form submit initiated');
    trackFormSubmit('tuition', undefined, { mode });

    const schoolInfo = buildSchoolInfo(values, isAdminCreate);
    const courseList = buildCourseList(values);
    const commentsList = buildCommentsList(values);
    const checkboxList = buildCheckboxList(values);

    const submitPayload = buildSubmitPayload(
      schoolInfo,
      courseList,
      commentsList,
      checkboxList
    );

    let errorCorrectionPayload = {};
    if (errorCorrectionMode) {
      const base = originalApiObject || formData;
      errorCorrectionPayload = buildErrorCorrectionPayload(base, values);
    }

    const handlers = {
      setShowSuccess,
      setShowAdminCreateApplicationError,
      setAdminCreateApplicationErrorMessage,
    };

    try {
      if (errorCorrectionMode) {
        return await handleErrorCorrection({
          dispatch,
          location,
          errorCorrectionPayload,
          navigate,
          ...handlers,
        });
      }
      if (isAdminCreate) {
        return await handleAdminSubmit(dispatch, values, submitPayload, handlers);
      }
      return await handleUserSubmit(dispatch, submitPayload, handlers);
    } catch (err) {
      console.error('Submission error:', err);
      setShowAdminCreateApplicationError(true);
      setAdminCreateApplicationErrorMessage(
        'An unexpected error occurred during submission'
      );
    }
  };

  const handleStep1Save = async (
    values: Partial<TuitionFormData>,
    fieldsToCheck: string[]
  ): Promise<boolean> => {

    // In admin mode, disable all fields before validation (apply same transformation as FormStepperTuition)
  let fieldsToValidate = errorCorrectionMode 
    ? personalInfoFields
        // .filter((field) => field.name !== 'alternateNuid')
        .map(f => ({
          ...f,
          disabled: true,
          required: false,
          validations: [],
        }))
    : personalInfoFields;
  
  // Filter out disabled fields to skip validation for them
  // fieldsToValidate = fieldsToValidate.filter(f => !f.disabled);
    const validationResult = validateForm(
      fieldsToValidate,
      values as Record<string, any>
    );

    if (!validationResult.isValid) {
      setValidationErrors(validationResult.errors);
      return false;
    }

    const isAnyFieldModified = fieldsToCheck.some(
      (field) => values?.[field] !== formData?.[field]
    );

    if (!isAnyFieldModified) {
      console.log("No changes detected in key fields, skipping save API call.");
      return true;
    }

    const toPersist = (newVal: any, existingVal: any) => {
      if (typeof newVal === "string") {
        const trimmed = newVal.trim();
        if (
          trimmed === "" ||
          trimmed === "--select--" ||
          trimmed.toLowerCase() === "select"
        ) {
          return null;
        }
      }
      // If the field is present (including null), prefer it
      if (newVal !== undefined) return newVal;
      // Otherwise fall back to existing value or null
      return existingVal ?? null;
    };
    const payload = {
      prefPhone: toPersist(values?.empPrefPhone, formData?.empPrefPhone),
      prefEmail: toPersist(values?.empPrefEmail, formData?.empPrefEmail),
      altPhone: toPersist(
        values?.empAlternatePhone,
        formData?.empAlternatePhone
      ),
      homeEmail: toPersist(values?.empHomeEmail, formData?.empHomeEmail),
    };
    try {
      if (isAdminCreate) {
        await dispatch(
          saveTuitionEmployeeDetailsAdmin({
            nuid: values?.empNuid,
            data: payload,
          })
        ).unwrap();
        await dispatch(
          fetchEmployeeDetailsAdmin({ empId: String(safeGet(formData?.empId, values?.empId)) })
        );
      } else {
        await dispatch(saveTuitionEmployeeDetails(payload)).unwrap();
        await dispatch(fetchTuitionEmployeeDetails());
      }
      return true;
    } catch (err) {
      console.error("Save failed", err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
       return false;
    }
  };

  const handleStep4Save = async (
    values: Partial<TuitionFormData>
  ): Promise<boolean> => {
    
    // Filter out applicationYear field from validation in error correction mode
    const fieldsToValidate = errorCorrectionMode 
      ? vendorInfoFields.filter(f => f.name !== 'applicationYear')
      : vendorInfoFields;
    
   
    
    const validationResult = validateTuitionForm(
      fieldsToValidate,
      values as Record<string, any>
    );

    // Fix: Remove applicationYear error for employee flow AND error correction mode (backup check)
    let errors = { ...validationResult.errors };
    if ((!isAdminCreate || errorCorrectionMode) && errors.applicationYear) {
      delete errors.applicationYear;
    }

    if (!validationResult.isValid && Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }

    return true;
  };

  const handleStep5Save = (values: Partial<TuitionFormData>): boolean => {
    const classCount = (values as any).classCount ?? 1;
    let allErrors: Record<string, string> = {};

    for (let classIndex = 0; classIndex < classCount; classIndex++) {
      const classFields = courseInfoFields.map((field) => {
        if (classIndex === 0) {
          return field;
        }

        // Clone the field and update the name with class suffix
        const updatedField = {
          ...field,
          name: `${field.name}_class${classIndex}`,
        };

        // Update compareField in validations if it exists (for date comparisons)
        if (field.validations) {
          updatedField.validations = field.validations.map((validation) => {
            if (validation.compareField) {
              return {
                ...validation,
                compareField: `${validation.compareField}_class${classIndex}`,
              };
            }
            return validation;
          });
        }

        return updatedField;
      });

      const validationResult = validateTuitionForm(
        classFields,
        values as Record<string, any>
      );

      if (!validationResult.isValid) {
        allErrors = { ...allErrors, ...validationResult.errors };
      }
    }

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      return false;
    }

    return true;
  };

  const handleStep6Save = (values: Partial<TuitionFormData>): boolean => {
    const validationResult = validateForm(
      commentsFields,
      values as Record<string, any>
    );

    if (!validationResult.isValid) {
      const allErrors = validationResult.requiredIfErrors
        ? { ...validationResult.errors, ...validationResult.requiredIfErrors }
        : { ...validationResult.errors };
      setValidationErrors(allErrors);
      return false;
    }

    return true;
  };

  const handleStep3SaveWrapper = async (
    values: Partial<TuitionFormData>
  ): Promise<boolean> => {
    return handleStep3Save(
      values,
      formData,
      dispatch,
      setValidationErrors,
      {
        setAlternateNuidInput,
        setIsSearchSuccessful,
        setClearAltNuidSignal,
      },
      approverInfoFields,
      isAdminCreate
    );
  };

  const handleSave = async (
    values: Partial<TuitionFormData>,
    currentStep?: number
  ): Promise<boolean | void> => {
    console.log("TuitionApplyContainer.handleSave called", {
      currentStep,
      values,
    });
    console.log(` Analytics: Tuition form save - Step ${currentStep}`)
    trackFormSave('tuition', currentStep, { mode: mode })

    const typedAltNuid = String(values?.alternateNuid ?? "").trim();
    if (typedAltNuid) setAlternateNuidInput(typedAltNuid);

    setValidationErrors({});

    const fieldsToCheck = [
      "empPrefPhone",
      "empPrefEmail",
      "empAlternatePhone",
      "empHomeEmail",
    ];

    let saveResult;
    switch (currentStep) {
      case 1:
        saveResult = await handleStep1Save(values, fieldsToCheck);
        break;
      case 3:
        saveResult = await handleStep3SaveWrapper(values);
        break;
      case 4:
        saveResult = await handleStep4Save(values);
        break;
      case 5:
        saveResult = handleStep5Save(values);
        break;
      case 6:
        saveResult = handleStep6Save(values);
        break;
      default:
        return;
    }
    
    // In error correction mode, persist user edits and only remove errors for fields that were actually changed and now pass validation
    if (saveResult !== false && errorCorrectionMode && currentStep && values) {
      const currentStepFieldNames = getCurrentStepFieldNames(currentStep);
      const updatedErrors = { ...errorCorrectionErrors };
      let updatedFormValues = { ...formValues } as Record<string, any>;
      const currentFormValues = formValues as Record<string, any>;
      let changed = false;
      currentStepFieldNames.forEach(fieldName => {
        if (values[fieldName] !== undefined && formValues) {
          // If the value changed, update formValues
          if (values[fieldName] !== currentFormValues[fieldName]) {
            updatedFormValues[fieldName] = values[fieldName];
            changed = true;
          }
          // Only remove error if the value changed AND the error is no longer present (i.e., passes validation)
          if (
            values[fieldName] !== currentFormValues[fieldName] &&
            !validationErrors[fieldName]
          ) {
            delete updatedErrors[fieldName];
          }
        }
      });
      if (changed && setFormValues) {
        setFormValues(updatedFormValues);
      }
      setErrorCorrectionErrors(updatedErrors);
    }
    
    return saveResult;
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    if (isAdminCreate) {
      navigate("/admin/tuition");
    } else {
      navigate("/tuition");
    }
  };

  const handleStepChange = () => {
  
    // In error correction mode, always restore the error correction errors
    // This ensures errors for other steps are preserved even if Form component clears them
    if (errorCorrectionMode) {
      setValidationErrors(errorCorrectionErrors);
    } else {
      setValidationErrors({});
    }
  };

  const handleClearValidationErrors = () => {
    if (errorCorrectionMode) {
      // Only clear error fields for the current step, keep errorCorrectionMode and errorCorrectionErrors
      // Find current step from FormStepperTuition (passed as prop)
      // We'll assume currentStep is tracked in FormStepperTuition and can be passed via a callback or refactor if needed
      // For now, clear all error fields (reset validationErrors to errorCorrectionErrors)
      setValidationErrors(errorCorrectionErrors);
      // Optionally, could clear only current step's errors if currentStep is available
    } else {
      setValidationErrors({});
      setErrorCorrectionMode(false);
      setErrorCorrectionErrors({});
    }
  };

  const handleUpdateValidationErrors = (errors: Record<string, string>) => {
    // If in error correction mode, always ensure error correction errors are present
    // This prevents the Form component from accidentally clearing errors for other steps
    if (errorCorrectionMode) {
      setValidationErrors({ ...errorCorrectionErrors, ...errors });
    } else {
      setValidationErrors(errors);
    }
  };

  const handleSearch = async (fieldName: string, value: any) => {
    if (fieldName === "alternateNuid" || fieldName === "altNuid") {
      const nuid = String(value ?? "").trim();
      setAlternateNuidInput(nuid);
      if (!nuid) {
        setIsSearchSuccessful(false);
        setClearAltNuidSignal((s) => s + 1);
        return;
      }
      try {
        const result = await dispatch(searchAltNuid(nuid));
        console.log(isSearchSuccessful, "isSearchSuccessful");
        console.log(result, alternateNuidInput, "resultalternateNuidInput");

        if (searchAltNuid.fulfilled.match(result)) {
          setIsSearchSuccessful(true);
          console.log("Alt NUID search successful:", result.payload);
          try {
            // Patch/update manager/employee record with the alternate NUID
            await dispatch(
              updateManagerDetails({ altManagerNuid: nuid })
            ).unwrap();
          } catch (err) {
            console.error(
              "Failed to update manager details after search:",
              err
            );
          } finally {
            // Refresh the employee details regardless of patch success
            dispatch(fetchTuitionEmployeeDetails());
          }
        } else if (searchAltNuid.rejected.match(result)) {
          setIsSearchSuccessful(false);
          console.error("Alt NUID search failed:", result.payload);
          setAlternateNuidInput("");
          setClearAltNuidSignal((s) => s + 1);
          dispatch(fetchTuitionEmployeeDetails());
        }
      } catch (err) {
        setIsSearchSuccessful(false);
        console.error("Alt NUID search error:", err);
        setAlternateNuidInput("");
        setClearAltNuidSignal((s) => s + 1);
        dispatch(fetchTuitionEmployeeDetails());
      }
    }
  };

  React.useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    if (altNuidError) {
      t = setTimeout(() => {
        dispatch(clearAltNuidError());
        dispatch(fetchTuitionEmployeeDetails());
      }, 5000);
    }
    return () => {
      if (t) clearTimeout(t);
    };
  }, [altNuidError, dispatch]);

  React.useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    if (altNuidResult) {
      t = setTimeout(() => {
        dispatch(clearAltNuidResult());
      }, 10000);
    }
    return () => {
      if (t) clearTimeout(t);
    };
  }, [altNuidResult, dispatch]);

  const initialValues: TuitionFormData = {
    ...formData,
    10151: "No",
    10150: "No",
  };

  const effectiveLoading = isAdminCreate
    ? Boolean(safeGet(adminLoading, employeeDetailsLoading))
    : Boolean(loading);

  return (
    <>
      {effectiveLoading && <Loader />}
      <TuitionApplyForm
        initialValues={errorCorrectionMode ? { ...formData, ...formValues } : initialValues}
        handleSubmit={handleSubmit}
        handleSave={handleSave}
        handleSearch={handleSearch}
        handleStepChange={handleStepChange}
        clearAltNuidSignal={clearAltNuidSignal}
        altNuidResult={altNuidResult}
        altNuidError={altNuidError}
        error={error}
        savePatchError={effectiveSavePatchError}
        updateManagerDetailsError={updateManagerDetailsError}
        showSuccess={showSuccess}
        onCloseSuccess={handleCloseSuccess}
        showAdminCreateApplicationError={showAdminCreateApplicationError}
        adminCreateApplicationErrorMessage={adminCreateApplicationErrorMessage}
        validationErrors={validationErrors}
        onClearValidationErrors={handleClearValidationErrors}
        onUpdateValidationErrors={handleUpdateValidationErrors}
        mode={mode}
        initialStep={initialStep}
        hideBreadcrumbs={errorCorrectionMode}
        showErrorCounts={errorCorrectionMode}
        isErrorCorrectionMode={errorCorrectionMode}
      />
    </>
  );
};

export default TuitionApplyContainer;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString || typeof dateString !== "string") {
    return "";
  }

  let month, day, year;

  if (dateString.includes("/")) {
    [month, day, year] = dateString.split("/");
  } else if (dateString.includes("-")) {
    [year, month, day] = dateString.split("-");
  } else {
    return "";
  }

   // Pad month and day to 2 digits
  const mm = month.padStart(2, "0");
  const dd = day.padStart(2, "0");
 
  return `${mm}-${dd}-${year}`;
};

const formatDatesInObject = (
  obj: Record<string, any>,
  dateFields: string[]
): Record<string, any> => {
  const formattedObj = { ...obj };
  dateFields.forEach((field) => {
    if (formattedObj[field]) {
      formattedObj[field] = formatDate(formattedObj[field]);
    }
  });
  return formattedObj;
};
